import React, { useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import { Search } from '@mui/icons-material';

import { CaseFilter } from '@apps/shared/src/caseSelect/actions';
import { defaultMemberState } from '@apps/shared/src/getMember/reducer';
import { MemberInfo } from '@apps/shared/src/getMember/types/memberTypes';

import { debounce } from '@apps/shared/src/utilities';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { IncompleteTheme } from '../shared/types/theme';

const styles = makeStyles((theme: IncompleteTheme) => ({
  filterContainer: {
    backgroundColor: '#ffffff',
    height: 'calc(100vh - 70px - 50px - 6rem)',
    padding: '0 1rem',
    zIndex: 0,
  },
  selectBox: {
    width: '100%',
    '& .MuiSelect-selectMenu': {
      backgroundColor: '#fff !important',
      border: '1px solid #E5EBF5',
      width: '100%',
    },
  },
  resetBtn: {
    color: '#ff0000',
    textDecoration: 'underline',
    padding: '0',
    border: 'none',
    backgroundColor: 'white',
    cursor: 'pointer',
  },
  root: {
    width: '100%',
    '& .MuiOutlinedInput-adornedStart': {
      paddingLeft: '0',
    },
    '& .MuiOutlinedInput-input': {
      height: '40px',
      padding: '0 10px 0 0',
    },
    '& .MuiIconButton-root': {
      paddingRight: '0',
    },
  },
  searchIcon: {
    color: '#F07037',
    fontSize: '22px',
    paddingLeft: '0',
  },
  menuItem: {
    backgroundColor: '#fff',
    color: 'red',
    height: '60px',
    width: '100%',
  },
  selectDiv: {
    width: '100%',
    marginTop: '30px',
  },
  label: {
    color: '#666',
    fontSize: '14px',
    fontFamily: 'Roboto, Sans-serif',
  },
}));

type ParentProps = {
  getMyCases: (filter: CaseFilter) => void;
  getCasesForClient: (filter: CaseFilter) => void;
  resetMyCasesState: () => void;
  appliedFilter: CaseFilter;
  selectedPlan: string;
  isClient: boolean;
  plans: any;
  setAppliedFilter: React.Dispatch<React.SetStateAction<any>>;
  updateSelectedPlan: (id: string) => void;
  updateCaseMemberDetail: (memberInfo: MemberInfo) => void;
  setPage: React.Dispatch<React.SetStateAction<any>>;
  setRowsPerPage: React.Dispatch<React.SetStateAction<any>>;
};

const MyCasesFilter = ({
  getMyCases,
  getCasesForClient,
  resetMyCasesState,
  appliedFilter,
  isClient,
  selectedPlan,
  plans,
  setAppliedFilter,
  updateSelectedPlan,
  updateCaseMemberDetail,
  setPage,
  setRowsPerPage,
}: ParentProps) => {
  const classes = styles();
  const debouncedSearch = useCallback(
    debounce(filteredValues => {
      if (isClient) {
        getCasesForClient(filteredValues);
      } else {
        getMyCases(filteredValues);
      }
      // Update pagination state
      setPage(0);
      setRowsPerPage(10);
    }, 3000),
    [getMyCases, getCasesForClient]
  );

  const handleSearch = (event: any) => {
    const { value } = event.target;

    setAppliedFilter((prevState: any) => ({
      ...prevState,
      queryParam: value,
    }));
    const filteredValues = {
      queryParam: value,
      type: appliedFilter.type === 'All' ? '' : appliedFilter.type,
      createdDate: '',
      status: appliedFilter.status === 'All' ? '' : appliedFilter.status,
      planID: appliedFilter.planID,
    };
    debouncedSearch(filteredValues);
  };

  const handleStatusChange = (event: any) => {
    const { value } = event.target;
    setAppliedFilter((prevState: any) => ({
      ...prevState,
      status: value,
    }));
    const filteredValues = {
      queryParam: appliedFilter.queryParam,
      type: appliedFilter.type === 'All' ? '' : appliedFilter.type,
      createdDate: '',
      status: value === 'All' ? '' : value,
      planID: appliedFilter.planID,
    };
    if (isClient) {
      getCasesForClient(filteredValues);
    } else {
      getMyCases(filteredValues);
    }
    setPage(0);
    setRowsPerPage(10);
  };

  const handleCaseTypeChange = (event: any) => {
    const { value } = event.target;
    setAppliedFilter((prevState: any) => ({
      ...prevState,
      type: value,
    }));
    const filteredValues = {
      queryParam: appliedFilter.queryParam,
      type: value === 'All' ? '' : value,
      createdDate: '',
      status: appliedFilter.status === 'All' ? '' : appliedFilter.status,
      planID: appliedFilter.planID,
    };
    if (isClient) {
      getCasesForClient(filteredValues);
    } else {
      getMyCases(filteredValues);
    }
    setPage(0);
    setRowsPerPage(10);
  };

  const resetFilter = () => {
    const initialState = {
      queryParam: '',
      type: 'All',
      createdDate: '',
      status: 'All',
      planID: selectedPlan,
    };
    resetMyCasesState();
    setAppliedFilter(initialState);
    const filteredValues = {
      queryParam: '',
      type: '',
      createdDate: '',
      status: '',
      planID: selectedPlan,
    };
    if (isClient) {
      getCasesForClient(filteredValues);
    } else {
      getMyCases(filteredValues);
    }
    setPage(0);
    setRowsPerPage(10);
  };

  const handlePlanChange = (event: any) => {
    const { value } = event.target;
    updateSelectedPlan(value);
    updateCaseMemberDetail(defaultMemberState.memberInfo);
    setPage(0);
    setRowsPerPage(10);
  };

  return (
    <>
      <div className={classes.filterContainer}>
        <div style={{ textAlign: 'right', margin: '1rem 0' }}>
          <button onClick={resetFilter} className={classes.resetBtn} type="button">
            Clear
          </button>
        </div>

        <div>
          <TextField
            id="outlined-basic"
            variant="outlined"
            placeholder="Search..."
            type="search"
            value={appliedFilter.queryParam}
            onChange={handleSearch}
            className={classes.root}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton aria-label="Search" size="large">
                    <Search className={classes.searchIcon} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>

        {isClient && plans.length > 0 && (
          <div className={classes.selectDiv}>
            <p className={classes.label}>Select Plan</p>
            <Select
              id="demo-simple-select-outlined"
              value={selectedPlan}
              onChange={handlePlanChange}
              className={classes.selectBox}
            >
              {plans.map((plan: any) => (
                <MenuItem key={plan.id} value={plan.id}>
                  {plan.name}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}

        <div className={classes.selectDiv}>
          <p className={classes.label}>Status</p>
          <Select
            id="demo-simple-select-outlined"
            value={appliedFilter.status}
            onChange={handleStatusChange}
            className={classes.selectBox}
          >
            <MenuItem value="All" className={classes.menuItem}>
              All
            </MenuItem>
            <MenuItem value="provider negotiation">Processing</MenuItem>
            <MenuItem value="intake">Opened</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </div>

        <div className={classes.selectDiv}>
          <p className={classes.label}>Case Type</p>
          <Select
            id="demo-simple-select-outlined"
            value={appliedFilter.type}
            onChange={handleCaseTypeChange}
            className={classes.selectBox}
          >
            <MenuItem value="All" className={classes.menuItem}>
              All
            </MenuItem>
            <MenuItem value="BalanceBill">Balance Bill</MenuItem>
            <MenuItem value="AccessIssue">Access Issue</MenuItem>
            <MenuItem value="CallAhead">Call Ahead</MenuItem>
            {isClient && <MenuItem value="Appeal">Appeal</MenuItem>}
          </Select>
        </div>
      </div>
    </>
  );
};

export default MyCasesFilter;
